"use client";
import React from "react";
import { cn } from "@gettactic/helpers";
import { SearchInput } from "@cmp-src/controls/SearchInput/SearchInput";
import { ChevronRight } from "lucide-react";
import { TacticIcon } from "@cmp-src/icons/TacticLogo/TacticIcon";
import { formatShortDate } from "@gettactic/helpers/src/date-time/formatShortDate";
import { dateStringToLocalTime } from "@gettactic/helpers/src/date-time/dateStringToLocalTime";
import { useAskTessa } from "@cmp-src/tessa/AskTessaProvider";
import { useTacticHooks } from "@gettactic/hooks";

export interface ChatBotSidebarProps {
  isBubble?: boolean;
  isTranslucent?: boolean;
  setPopupClosed?: () => void;
}

export const ChatBotSidebar: React.FC<ChatBotSidebarProps> = ({
  isBubble,
  isTranslucent,
  setPopupClosed
}) => {
  const { chats, selectedChatId, setSelectedChatId, newChatItem } =
    useAskTessa() || {
      chats: { elements: [] },
      selectedChatId: undefined,
      setSelectedChatId: () => {},
      newChatItem: null
    };
  const { queryClient } = useTacticHooks();
  const [searchQuery, setSearchQuery] = React.useState<string>("");

  const elements = React.useMemo(() => {
    const elements = [...(chats?.elements || [])];

    if (newChatItem?.id && !elements.find((e) => e.id === newChatItem.id)) {
      elements.unshift({
        id: newChatItem.id,
        title: newChatItem.title,
        created: "",
        last_message: ""
      });
    }

    if (!elements.length) return [];
    if (!searchQuery || searchQuery.length < 3) return elements;

    return elements.filter((element) => {
      return (element.title || element.id)
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
    });
  }, [chats?.elements, searchQuery, selectedChatId, newChatItem?.id]);

  const handleSelectChatId = (chatId: string) => {
    queryClient.refetchQueries({ queryKey: ["tessa.chat.messages", chatId] });
    setSelectedChatId(chatId);
    setPopupClosed?.();
  };

  const chatItems = (
    <div className={cn("flex flex-col gap-2", !isBubble && "px-2")}>
      {elements.length > 0
        ? elements.map((chat) =>
            isBubble ? (
              <button
                className={cn(
                  "flex w-full min-w-0 truncate border-b border-b-neutral-100 p-2"
                )}
                onClick={() => {
                  handleSelectChatId(chat.id);
                }}
                key={chat.id}
              >
                <span className={"flex flex-1 items-center gap-4"}>
                  <div className={"w-8"}>
                    <TacticIcon className={"size-8"} />
                  </div>
                  <span className={"flex flex-col gap-1 text-left"}>
                    <span className={"block font-bold"}>
                      {chat.title || chat.id}
                    </span>
                    <span className={"block"}>
                      {formatShortDate(
                        new Date(dateStringToLocalTime(chat.last_message))
                      )}
                    </span>
                  </span>
                </span>
                <span>
                  <ChevronRight />
                </span>
              </button>
            ) : (
              <button
                className={cn(
                  "w-full min-w-0 truncate rounded-md border-1 border-transparent px-2 py-2 text-left transition-colors hover:bg-accent",
                  selectedChatId === chat.id && "bg-neutral-200",
                  isTranslucent && "bg-white/30 backdrop-blur-md"
                )}
                onClick={() => {
                  handleSelectChatId(chat.id);
                }}
                key={chat.id}
              >
                {chat.title || chat.id}
              </button>
            )
          )
        : undefined}
    </div>
  );

  return (
    <div className={"absolute inset-0 flex flex-col"}>
      <div className={"py-4"} />
      <div
        className={!isBubble ? "relative flex flex-1 flex-col px-4" : undefined}
      >
        {!isBubble && (
          <div
            className={"mb-4 flex w-full items-center justify-center px-2.5"}
          >
            <SearchInput
              className={"mx-0 w-full border-0 bg-white/80"}
              query={searchQuery}
              setQuery={setSearchQuery}
              placeholder={"Search chats"}
              fullWidth
            />
          </div>
        )}

        <div className={"text-sm text-neutral-800 mt-12 mb-2.5 px-4"}>
          Chat History
        </div>
        {chatItems}
      </div>
    </div>
  );
};
